/* global GM_STATE */
import { emitterProps, globalEmitter } from 'helpers/emitter-event'
import { updateCart, openMiniCart, klaviyoTrackingAddToCart, syncPreorderCartProperties } from 'libs/cart'
import { handlePromoDiscounts } from 'libs/shopify'
import { useEffect, useMemo, useRef, useState } from 'preact/hooks'
import { delay } from 'helpers/utils'
import MiniCartList from 'snippets/mini-cart-list/mini-cart-list'
import MiniCartButton from 'snippets/mini-cart-button/mini-cart-button'
import LoaderSpin from 'snippets/loader-spin/loader-spin'
import useCartGlobal from 'hooks/useCartGlobal'
import CartRelated from 'modules/cart-related/cart-related'
import CartPromo from 'snippets/cart-promo/cart-promo'
import { hideModal } from 'helpers/dom'

const selectors = {
  MINI_CART_OPEN_CLASS: 'mini-cart-open'
}

function MiniCart() {
  const { cart, setCart, isFetching, setIsFetching, isLoaded, setIsLoaded } = useCartGlobal()
  const [appliedDiscountCodes, setAppliedDiscountCodes] = useState([])
  const [promoVersion, setPromoVersion] = useState(0)
  const isSyncingRef = useRef(false)

  useEffect(() => {
    const validCodes = handlePromoDiscounts(cart.items)
    setAppliedDiscountCodes(validCodes)
    setPromoVersion((prev) => prev + 1) // force CartPromo to refresh

    // Don't race qty changes, and don't stack syncs on every re-render
    if (isFetching || isSyncingRef.current || !cart?.items?.length) return

    // The sync rewrites line properties, which changes their keys -- reload so the rows aren't
    // left holding dead keys.
    isSyncingRef.current = true
    syncPreorderCartProperties(cart.items)
      .then(async (didWriteToCart) => {
        if (!didWriteToCart) return
        const newCart = await updateCart()
        setCart(newCart)
        GM_STATE.cart.initCart = newCart
        globalEmitter.emit(emitterProps.cart.update, newCart.items)
      })
      .finally(() => {
        isSyncingRef.current = false
      })
  }, [cart, isFetching])

  // Initialize isLoaded on mount to prevent infinite loading
  useEffect(() => {
    const initOnMount = async () => {
      if (!isLoaded && cart?.items) {
        await delay(500)
        setIsLoaded(true)
      }
    }
    initOnMount()
  }, [])

  const init = async () => {
    if (!isLoaded) {
      await delay(500)
      setIsLoaded(true)
    }
  }

  const render = async () => {
    const newCart = await updateCart()
    setCart(newCart)
    klaviyoTrackingAddToCart(newCart)
    openMiniCart()
    await init()
    GM_STATE.cart.initCart = newCart
    globalEmitter.emit(emitterProps.cart.update, newCart.items)
  }

  useEffect(() => {
    globalEmitter.on(emitterProps.cart.add, async (value) => value && (await render()))
    globalEmitter.on(emitterProps.cart.trigger, async () => await render())
    globalEmitter.on(emitterProps.cart.render, async (value) => {
      document.body.classList.add(selectors.MINI_CART_OPEN_CLASS)
      value && (await init())
    })
    globalEmitter.on(emitterProps.modal.close, (element) => {
      if (element?.dataset.target === 'MiniCart') {
        document.body.classList.remove(selectors.MINI_CART_OPEN_CLASS)
      }
    })
  }, [])

  const cartItemId = useMemo(() => cart.items?.length > 0 && cart.items[0].product_id, [cart])

  const closeModal = () => hideModal('MiniCart')

  return (
    <div className={`relative mb-6 flex h-[calc(100%-57px)] flex-col ${cart.item_count === 0 ? 'border-t border-default' : ''}`}>
      <div class="js-mini-cart-content overflow-y-overlay h-full overflow-x-hidden flex flex-col justify-end">
        <CartPromo
          cart={cart}
          discountCodes={appliedDiscountCodes}
          isMiniCart={true}
          key={promoVersion}
        />
        <form
          action={`/cart${appliedDiscountCodes.length ? `?discount=${appliedDiscountCodes.join(',')}` : ''}`}
          method="POST"
          id="cart"
          className="flex flex-1 h-auto flex-col overflow-auto"
        >
          {cart && cart.items.length > 0 ? (
            isLoaded ? (
              <>
                <MiniCartList items={cart.items} setCart={setCart} setIsFetching={setIsFetching} isFetching={isFetching} />
                <div className="mt-auto">
                  <CartRelated id={cartItemId} />
                </div>
                <MiniCartButton cart={cart} isFetching={isFetching} isDisplayDiscountNote={!!appliedDiscountCodes.length} />
              </>
            ) : (
              <div className="flex items-center justify-center">
                <LoaderSpin width="10" height="10" />
              </div>
            )
          ) : (
            <div className="container col-span-2 bg-white">
              <div class="py-12 text-center md:py-20 xl:py-10">
                <div className="h3 text-2xl">{translate.cart.empty_cart}</div>
                <p className="mb-4 mt-2 text-sm text-grey-700">{translate.cart.empty_description}</p>
                <button type='button' title={translate.cart.continue_shopping} onClick={closeModal} className="button-outlined js-close-modal-dialog">{translate.cart.continue_shopping}</button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  )
}

export default MiniCart
